import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

type FallbackRender = (props: FallbackProps) => ReactNode;

interface Props {
  children: ReactNode;
  fallback?: ReactNode | FallbackRender;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can also log the error to an error reporting service
    console.error('ErrorBoundary caught an error', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = (): void => {
    // Reset error state and reload the component
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Reload the page as a last resort
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      captureException(this.state.error);

      // If a fallback is provided and it's a function, call it with the error props
      if (fallback && typeof fallback === 'function') {
        return fallback({
          error: this.state.error as Error,
          resetErrorBoundary: this.handleRetry,
        });
      }

      // If a ReactNode fallback is provided, render it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <Dialog open={true}>
          <DialogContent
            className="w-11/12 bg-primary py-10 text-center sm:w-full"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader className="flex w-full flex-col items-center">
              <DialogTitle className="pb-6">Something went wrong!</DialogTitle>
              <DialogDescription className="flex w-full grow-0 flex-col items-center overflow-hidden">
                <div className="mb-4">
                  The application encountered an unexpected error.
                </div>
                {this.state.error && (
                  <div className="mt-4 w-full rounded-lg bg-black/10 p-4 text-left font-mono text-sm">
                    <p className="font-semibold text-red-500">
                      {this.state.error.toString()}
                    </p>
                    {/* {this.state.errorInfo && (
                      <div className="w-full overflow-hidden">
                        <pre className="mt-2 max-h-40 whitespace-pre-wrap text-xs opacity-70">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )} */}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                type="submit"
                onClick={this.handleRetry}
                variant="secondary"
                animation="scale"
              >
                Try Again
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
